import { useEffect, useMemo, useState } from 'react';
import {
  useSearchTerms,
  useSaveSearchTerms,
  useSearchTermSuggestions,
} from '../hooks/useSearchTerms';
import type { SearchTermRollUp } from '../api/searchTermsApi';
import { SEARCH_TERMS_INPUT_HINT } from '../utils/searchTermsCopy';
import styles from './SearchTermsPicker.module.css';

interface SearchTermsPickerProps {
  locationId: string;
  /** Set when an agency user edits a practice they manage; routes the reads/writes through /agency. */
  practiceId?: string;
  /** Save the Search Console suggestions as version 1 when the location has no set yet. */
  autoSaveSuggestions?: boolean;
  /** Called after a successful save, for hosts that show data derived from the saved set. */
  onSaved?: () => void;
}

// Module-level (not component state): guards the auto-save below against React StrictMode's dev-only
// double-mount, which resets any component-local ref/state on the synthetic remount but not module
// state. Without this, StrictMode would fire two real save requests for the same location's first
// suggestion. Keyed by locationId; naturally bounded to locations visited this session.
const autoSaveAttempted = new Set<string>();

const MAX_SUGGESTIONS_SHOWN = 8;

const sameTerm = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** "12 searches · 340 impr." plus a hover list of the top variants, or null with no GSC data. */
function variantSummary(
  rollUp: SearchTermRollUp | undefined,
): { label: string; title: string } | null {
  if (!rollUp || rollUp.variants.length === 0) return null;
  const count = rollUp.variants.length;
  const top = rollUp.variants
    .slice(0, 5)
    .map((v) => `${v.query} (${v.impressions.toLocaleString()})`)
    .join('\n');
  return {
    label: `${count} ${count === 1 ? 'search' : 'searches'} · ${rollUp.impressions.toLocaleString()} impr.`,
    title: `Search Console searches counted for this term, last 28 days:\n${top}`,
  };
}

export function SearchTermsPicker({
  locationId,
  practiceId,
  autoSaveSuggestions = false,
  onSaved,
}: SearchTermsPickerProps) {
  const scope = { locationId, practiceId };
  const { data: current, isLoading: searchTermsLoading } = useSearchTerms(scope);
  const { data: suggestions, isLoading: suggestionsLoading } = useSearchTermSuggestions(scope);
  const saveMutation = useSaveSearchTerms(scope, onSaved);
  const [draft, setDraft] = useState<string[]>([]);
  const [text, setText] = useState('');

  // A saved SearchTermSet always wins — the user owns it from here on, and a later GSC refresh must
  // never silently overwrite a curated list. Only a location with NO saved version at all
  // (`current === null`) gets the auto-suggested services — saved immediately as version 1, not
  // left as an unsaved draft, because saving is also this app's hire gate
  // (`hasActiveSearchTermSet`, agents.service.ts): requiring a manual click here would mean
  // assigning Sam to this location fails, from the agency command center, on a page the practice
  // may never have visited. The saved list comes back canonicalized by the server (location words
  // stripped, duplicates dropped), and this effect shows that version.
  useEffect(() => {
    if (searchTermsLoading) return;
    if (current) {
      setDraft(current.queries);
      return;
    }
    if (
      !autoSaveSuggestions ||
      suggestionsLoading ||
      !locationId ||
      autoSaveAttempted.has(locationId)
    )
      return;
    if (!suggestions?.length) return; // nothing to suggest yet (e.g. no GSC data) — leave manual entry as-is
    autoSaveAttempted.add(locationId);
    setDraft(suggestions); // instant feedback; the save below persists it as version 1
    saveMutation.mutate(suggestions);
  }, [
    current,
    searchTermsLoading,
    suggestions,
    suggestionsLoading,
    autoSaveSuggestions,
    locationId,
  ]);

  const rollUpByTerm = useMemo(
    () => new Map((current?.performance ?? []).map((p) => [p.term, p])),
    [current],
  );
  const unusedSuggestions = (suggestions ?? [])
    .filter((s) => !draft.some((d) => sameTerm(d, s)))
    .slice(0, MAX_SUGGESTIONS_SHOWN);

  const addTerm = (term: string) => {
    const trimmed = term.trim();
    if (!trimmed || draft.some((d) => sameTerm(d, trimmed))) return;
    setDraft([...draft, trimmed]);
  };
  const addTyped = () => {
    addTerm(text);
    setText('');
  };
  const removeQuery = (q: string) => setDraft(draft.filter((d) => d !== q));

  return (
    <div className={styles.panel}>
      <div className={styles.inputRow}>
        <input
          className={styles.input}
          placeholder="Add a service, e.g. emergency dentist"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTyped()}
        />
        <button className={styles.addButton} onClick={addTyped}>
          Add
        </button>
      </div>
      <p className={styles.hint}>{SEARCH_TERMS_INPUT_HINT}</p>
      <ul className={styles.list}>
        {draft.map((q) => {
          const summary = variantSummary(rollUpByTerm.get(q));
          return (
            <li key={q} className={styles.pill} title={summary?.title}>
              {q}
              {summary && <span className={styles.pillMeta}>{summary.label}</span>}
              <button
                aria-label={`Remove ${q}`}
                onClick={() => removeQuery(q)}
                className={styles.removeButton}
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>
      {unusedSuggestions.length > 0 && (
        <div className={styles.suggestions}>
          <span className={styles.hint}>Suggested from Search Console:</span>
          <ul className={styles.list}>
            {unusedSuggestions.map((s) => (
              <li key={s}>
                <button className={styles.suggestionPill} onClick={() => addTerm(s)}>
                  + {s}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        className={styles.saveButton}
        disabled={draft.length === 0 || saveMutation.isPending}
        onClick={() => saveMutation.mutate(draft)}
      >
        Save search terms
      </button>
      {saveMutation.isError && (
        <p className={styles.errorNote}>Could not save your search terms. Try again.</p>
      )}
    </div>
  );
}
